import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BellIcon } from '../icons/bell';
import { bp, palette } from '../../style-utils/theme';
import { useNotifications } from './notifications-context';
import { AppNotification } from '../../api/notifications-client';
import { ModalPortal } from '../modal/modal-portal';
import {
  DISTANCE,
  Presence,
  STAGGER,
  StatusLabel,
  T,
  overlayMotion,
  scrimMotion,
} from '../../motion';
import {
  Badge,
  BellButton,
  BellWrapper,
  EmptyState,
  HeaderActions,
  Item,
  ItemBody,
  ItemText,
  ItemTime,
  ItemTitle,
  LiveDot,
  Panel,
  PanelPlacement,
  PanelFooter,
  PanelHeader,
  PanelList,
  PanelTitle,
  Scrim,
  SeverityDot,
  TextButton,
} from './notification-bell.styled';
import { formatIn, resolveClock } from '../../function-utils/building-time';
import { useClockRole, useClockSurface, zoneOfBuilding } from '../../context/clock-context';

/**
 * The bell.
 *
 * Reads from the app-wide notifications provider — it holds no state of its
 * own beyond "is the panel open", so the staff rail and the resident dashboard
 * can both render one without opening a second socket or double-counting.
 */

/**
 * Relative time, in the product's voice. Deliberately coarse: "2h ago" is what
 * somebody wants from a notification list, and a precise timestamp on every row
 * is noise. The exact time is on the linked record.
 */
const relativeTime = (iso: string, zone: string): string => {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const seconds = Math.max(0, Math.round((Date.now() - then) / 1000));

  if (seconds < 60) return 'Just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;

  // Older than a week: a date, on the READER's clock by who they are (R-T12) —
  // their own device's for an owner or a manager, the building's for a
  // concierge or a resident. (The TEXT a notification carries was written by
  // the server with nobody reading; it names its own zone and is left as sent.)
  return formatIn(iso, zone, { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// Distance between the bell and the popover, in px.
const GAP = 8;

/* The provider pages by 20 (notifications-context PAGE_SIZE). The cascade is
   keyed on the index WITHIN a page, not the absolute index, so the fortieth row
   — appended by "Show older" — arrives with the same short delay as the first
   and not a second and a half later. */
const PAGE_SIZE = 20;

/* The sheet shape holds under `md` (theme.bp), where the rail is a bottom tab
   bar; matches the styled component's `down('md')` block exactly. */
const SHEET_QUERY = `(max-width: ${bp.md - 0.02}px)`;

const readSheet = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia(SHEET_QUERY).matches;

const useIsSheet = (): boolean => {
  const [sheet, setSheet] = useState(readSheet);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const mql = window.matchMedia(SHEET_QUERY);
    const onChange = () => setSheet(mql.matches);

    onChange();
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);

      return () => mql.removeEventListener('change', onChange);
    }
    mql.addListener(onChange);

    return () => mql.removeListener(onChange);
  }, []);

  return sheet;
};

interface NotificationBellProps {
  /**
   * Where the panel anchors. 'rail' (the default) is the staff shell's vertical
   * icon rail; 'header' is a horizontal top bar, such as the resident
   * dashboard's. Both become a bottom sheet on phones.
   */
  placement?: PanelPlacement;
}

export const NotificationBell: React.FC<NotificationBellProps> = ({
  placement = 'rail',
}) => {
  const navigate = useNavigate();
  /* WHOSE CLOCK A NOTIFICATION'S DATE IS ON (R-T12): the reader's, by who they
     are and where the bell is drawn — an owner's or a manager's own device, a
     concierge's or a resident's building (the notification's own, by id). */
  const surface = useClockSurface();
  const role = useClockRole();
  const zoneFor = (item: AppNotification): string =>
    resolveClock({
      surface,
      role,
      buildingZone: zoneOfBuilding(item.building_id),
    }).zone;
  const {
    notifications,
    unread,
    loading,
    nextCursor,
    connected,
    loadMore,
    markRead,
    markAllRead,
    refresh,
  } = useNotifications();

  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const isSheet = useIsSheet();

  // Where the portalled popover sits, measured from the bell. A fixed panel at
  // <body> has no header or rail as a containing block, so "below the bell"
  // and "to its right" have to be numbers. Re-measured on scroll and resize so
  // it stays glued to the bell.
  const [anchor, setAnchor] = useState<{
    top?: number;
    left?: number;
    right?: number;
  }>({});

  useEffect(() => {
    if (!open) return undefined;

    const place = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;

      setAnchor(
        placement === 'header'
          ? {
              top: Math.round(rect.bottom + GAP),
              right: Math.round(window.innerWidth - rect.right),
            }
          : {
              top: Math.round(rect.top),
              left: Math.round(rect.right + GAP),
            }
      );
    };

    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);

    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, placement]);

  // Close on outside click and on Escape. Both, not either: the panel is opened
  // from a rail that is often driven by keyboard at a desk, and a popover that
  // can only be dismissed with the mouse traps that user.
  //
  // The panel is portalled, so "inside" cannot be answered by the wrapper's
  // contains() alone — a press inside the panel is found by walking up to its
  // data-bell-panel marker instead.
  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      const element = target instanceof Element ? target : target?.parentElement;
      if (element?.closest('[data-bell-panel]')) return;
      const node = wrapperRef.current;
      if (node && target && !node.contains(target)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      // Focus goes back where it came from, or the next Tab starts at the top
      // of the document.
      buttonRef.current?.focus();
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const toggle = useCallback(() => {
    // Opening is the moment to re-read: the list may have been built before
    // the socket connected, or while the tab was asleep. Called HERE, not
    // inside the setOpen updater: an updater runs during render, and refresh()
    // sets the notifications provider's state — React's "Cannot update a
    // component while rendering a different component" in the dev build.
    if (!open) refresh();
    setOpen(!open);
  }, [open, refresh]);

  /**
   * Opening a notification marks it read and follows its link.
   *
   * READ IS SET ON OPEN, NOT ON RENDER. A row scrolling past in a panel is not
   * evidence anybody read it, and marking on render would empty the badge for
   * somebody who glanced at the bell and closed it again.
   */
  const openNotification = useCallback(
    (item: AppNotification) => {
      if (!item.read_at) markRead([item.id]);
      setOpen(false);
      if (!item.link) return;
      // A billing notice links to Stripe's hosted invoice page — an absolute
      // URL on another origin. The router would treat it as a path inside the
      // app and land on the 404 page, so it leaves the app instead.
      if (/^https?:\/\//i.test(item.link)) {
        window.location.assign(item.link);

        return;
      }
      navigate(item.link);
    },
    [markRead, navigate]
  );

  /**
   * PAGINATION THAT DOES NOT REQUIRE FINDING A BUTTON.
   *
   * "Show older" stays — it is the honest control, and it is what a keyboard
   * user reaches — but nobody scrolls to the bottom of a list and then hunts
   * for a link to continue. Loading as the end comes into view is what makes a
   * long history feel like one list instead of a series of pages.
   *
   * The 64px margin fires it just BEFORE the bottom, so the next page is
   * usually already in place by the time the scroll gets there. Guarded on
   * `loading` as well as on `nextCursor` so a slow page cannot be requested
   * twice by two scroll events in the same tick.
   */
  const onListScroll = useCallback(
    (event: React.UIEvent<HTMLUListElement>) => {
      if (!nextCursor || loading) return;
      const el = event.currentTarget;
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 64) {
        loadMore();
      }
    },
    [nextCursor, loading, loadMore]
  );

  // A popover grows out of the bell; the phone sheet rises from the bottom
  // edge. Both leave the way they came (the conditional is inside a Presence).
  const panelMotion = overlayMotion(isSheet ? 'sheet' : 'popover');

  return (
    <BellWrapper ref={wrapperRef} data-testid="bell-dock">
      <BellButton
        ref={buttonRef}
        type="button"
        isOpen={open}
        onClick={toggle}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={
          unread > 0
            ? `Notifications, ${unread} unread`
            : 'Notifications'
        }
      >
        <BellIcon stroke={open ? palette.iris : palette.slate} />
        {unread > 0 ? (
          // aria-hidden: the count is already in the button's label, and a
          // screen reader announcing "12" on its own says nothing.
          <Badge aria-hidden="true">{unread > 99 ? '99+' : unread}</Badge>
        ) : null}
      </BellButton>

      <Presence>
        {open ? (
          <ModalPortal key="notification-panel">
            <Scrim {...scrimMotion} onClick={() => setOpen(false)} />
            <Panel
              {...panelMotion}
              placement={placement}
              $top={anchor.top}
              $left={anchor.left}
              $right={anchor.right}
              data-bell-panel
              role="dialog"
              aria-label="Notifications"
            >
              <PanelHeader>
                <PanelTitle>Notifications</PanelTitle>
                <HeaderActions>
                  <LiveDot
                    live={connected}
                    title={connected ? 'Live' : 'Reconnecting…'}
                  />
                  <TextButton
                    type="button"
                    onClick={markAllRead}
                    disabled={unread === 0}
                  >
                    Mark all read
                  </TextButton>
                </HeaderActions>
              </PanelHeader>

              <PanelList onScroll={onListScroll}>
                {notifications.length === 0 && !loading ? (
                  <EmptyState>
                    Nothing yet. Parcels, visitors and handovers will show up here.
                  </EmptyState>
                ) : null}

                {notifications.map((item, index) => (
                  <Item
                    key={item.id}
                    unread={!item.read_at}
                    /* A muted type still keeps its row — the mute stops the
                       delivery, not the record — so it is rendered quieter
                       rather than hidden. The rest opacity is the animation's
                       target, because framer's inline value would override a
                       stylesheet one. */
                    initial={{ opacity: 0, y: DISTANCE.small }}
                    animate={{ opacity: item.suppressed ? 0.55 : 1, y: 0 }}
                    transition={{
                      ...T.enterFast,
                      delay:
                        Math.min(index % PAGE_SIZE, STAGGER.maxItems) *
                        STAGGER.list,
                    }}
                    tabIndex={0}
                    role="button"
                    onClick={() => openNotification(item)}
                    onKeyDown={event => {
                      if (event.key !== 'Enter' && event.key !== ' ') return;
                      event.preventDefault();
                      openNotification(item);
                    }}
                  >
                    <SeverityDot severity={item.severity} />
                    <ItemBody>
                      <ItemTitle unread={!item.read_at}>{item.title}</ItemTitle>
                      {item.body ? <ItemText>{item.body}</ItemText> : null}
                      <ItemTime dateTime={item.created_at}>
                        {relativeTime(item.created_at, zoneFor(item))}
                      </ItemTime>
                    </ItemBody>
                  </Item>
                ))}
              </PanelList>

              {nextCursor ? (
                <PanelFooter>
                  <TextButton type="button" onClick={loadMore} disabled={loading}>
                    {/* The label swaps without the button changing width. */}
                    <StatusLabel
                      state={loading ? 'busy' : 'idle'}
                      labels={{ idle: 'Show older', busy: 'Loading…' }}
                    />
                  </TextButton>
                </PanelFooter>
              ) : notifications.length > 0 && !loading ? (
                // The END of the history, said once. Without it a list that has
                // simply run out is indistinguishable from one whose next page
                // failed to load, and somebody keeps pulling at it.
                <PanelFooter>
                  <TextButton type="button" disabled>
                    That’s everything
                  </TextButton>
                </PanelFooter>
              ) : null}
            </Panel>
          </ModalPortal>
        ) : null}
      </Presence>
    </BellWrapper>
  );
};
